import { BUTTON_SIZE, BUTTON_TYPE, DSBottomSheet, DSButton, DSTooltip, useToast } from '@repo/design-system/index'
import { QIcon_check_filled, QIcon_check_outline } from '@repo/icons/index'
import { useQuery } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useEventTracking } from '../../../helpers/gtmHelper'
import { CurrentUser } from '../../../model/Common/common.interface'
import apis from '../../../modules/apis'
import { axiosPost } from '../../../modules/axiosHelper'
import { useCurrentUser } from '../../../modules/context/UserContext'
import useIsMobile from '../../../modules/hooks/common/useIsMobile'
import useIsWebview from '../../../modules/hooks/common/useIsWebview'
import { n_lesson_prep, s_materials } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import { PRE_STUDY } from '../../../modules/vars'
import { fetchMaterial } from '../../../pages/[lang]/portal/materials/detail/[materialId]'
import { useSidePopup } from '../../basic/popup/SidePopup'
import EmptyBlock from '../../common/EmptyBlock'
import ExpressionBody from './ExpressionBody'
import MaterialInsightTitleSection from './MaterialInsightTitleSection'
import MaterialRelatedContents from './MaterialRelatedContents'
import MaterialTextBody from './MaterialTextBody'
import MaterialTutorSection from './MaterialTutorSection'
import MediaPanel from './MediaPanel'
import { fetchData } from './QuestionSelect/QuestionSelectPanel'

const InsightButtonReadButton = ({ materialData, setTabData, isModal }) => {
  const { t } = useTranslation()
  const router = useRouter()
  const { currentUser } = useCurrentUser()
  const { toastSuccess } = useToast()
  const { sendClickEvent } = useEventTracking()
  const lessonId = router.query?.lessonId
    ? typeof router.query.lessonId === 'string'
      ? parseInt(router.query.lessonId, 10)
      : 0
    : undefined

  const [insightButtonOpen, setInsightButtonOpen] = useState(true)
  const [checked, setChecked] = useState(
    materialData?.prestudy?.list.find((item) => item.type == PRE_STUDY.INSIGHT)?.value || false
  )
  // 인사이트 읽기 완료 버튼
  const onClickCheckInsight = useCallback(
    async (e) => {
      e.preventDefault()
      if (materialData) {
        // 인사이트 읽기 완료 버튼 tracking
        sendClickEvent({
          button_name: 'lesson_prep_insight_complete',
        })
        const payload = {
          action_type: PRE_STUDY.INSIGHT,
        }
        if (!checked) {
          const response = await axiosPost(apis.lessons.preparation.prestudy(materialData.id), payload)
          if (response) {
            setChecked(true)
            toastSuccess(t(n_lesson_prep.insight_done_toast))

            // 수업 준비 완료 status 확인
            const newData = await fetchData(lessonId)
            setTabData(newData?.lesson)
          }
        }
      }
    },
    [currentUser?.by_parent, materialData]
  )
  return (
    <div className="mb-[40px] flex w-full justify-start">
      <div className="min-w-4 flex h-4 items-center"></div>
      <div className="relative mx-auto flex flex-col">
        {insightButtonOpen && !checked ? (
          <DSTooltip
            place={'top'}
            triggerComponent={
              <DSButton
                onClick={onClickCheckInsight}
                size={BUTTON_SIZE.MD}
                type={BUTTON_TYPE.OUTLINE}
                className="items-center space-x-8"
              >
                {checked ? (
                  <QIcon_check_filled className="text-green-500" />
                ) : (
                  <QIcon_check_outline className="text-gray-300" />
                )}
                <span className="text-16 font-bold text-black">{t(n_lesson_prep.insight_done)}</span>
              </DSButton>
            }
            contentComponent={t(n_lesson_prep.overview.tooltip_insight)}
            hasCloseButton
            onClose={() => setInsightButtonOpen(false)}
          />
        ) : (
          <DSButton
            onClick={onClickCheckInsight}
            size={BUTTON_SIZE.MD}
            type={BUTTON_TYPE.OUTLINE}
            className="items-center space-x-8"
          >
            {checked ? (
              <QIcon_check_filled className="text-green-500" />
            ) : (
              <QIcon_check_outline className="text-gray-300" />
            )}
            <span className="text-16 font-bold text-black">{t(n_lesson_prep.insight_done)}</span>
          </DSButton>
        )}
      </div>
      <div className="min-w-4 flex h-4 items-center"></div>
    </div>
  )
}
type InsightPanelPropsType = {
  materialData: any
  materialId: string | string[]
  currentUser?: CurrentUser
  isLesson?: boolean
  setTabData?: any
  isInsightButton?: boolean
  isModal?: boolean
  setShowNav?: any
}

const MaterialInsightBody: React.FC<InsightPanelPropsType> = ({
  materialData,
  materialId,
  isLesson = false,
  setTabData,
  isInsightButton = false,
  isModal = false,
  setShowNav,
}) => {
  console.log(materialData, 'materialData')
  const { t } = useTranslation()
  const isMobile = useIsMobile(undefined, 1040)
  const sidePopup = useSidePopup()
  const isFreeTalkingOrWriting = materialData.course?.free_talking || materialData.course?.is_writing
  const isPracticeCategory =
    materialData?.course?.category.includes('#Speaking Practice') ||
    materialData?.course?.category.includes('#Business Practice')
  const [currentHash, setCurrentHash] = useState('')
  const [showBottomSheet, setShowBottomSheet] = useState(false)
  const docRef = useRef(null)
  const [prevScroll, setPrevScroll] = useState(0)
  const isWebview = useIsWebview()

  // 미디어 관련
  const media = {
    youtubes: materialData?.course?.video,
    articles: materialData?.course?.article,
  }
  const noMedia = media.youtubes?.length == 0 && media.articles?.length == 0
  const stringMateriId = String(materialId)

  // 교재 인사이트 탭 데이터
  const { data: insightData, isLoading }: any = useQuery({
    queryKey: ['material', stringMateriId],
    queryFn: () => fetchMaterial(stringMateriId),
  })
  console.log(insightData, 'insightDatainsightDatainsightData')
  const handleScroll = () => {
    if (!isWebview) return
    if (typeof setShowNav !== 'function') return

    const currentScroll = docRef.current.scrollTop
    if (currentScroll > prevScroll) {
      setShowNav(false)
    } else {
      setShowNav(true)
    }
    setPrevScroll(docRef.current?.scrollTop)
  }

  useEffect(() => {
    const handleHashChange = () => {
      const currentHash = window.location.hash.replace(/[^0-9]/g, '') // only number

      setCurrentHash(currentHash)
    }

    window.addEventListener('hashchange', handleHashChange)
    return () => {
      window.removeEventListener('hashchange', handleHashChange)
    }
  }, [])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, true)
    return () => {
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [prevScroll])

  useEffect(() => {
    const targetHash = currentHash || window.location.hash.replace(/[^0-9]/g, '')

    if (isMobile) {
      if (targetHash) {
        setShowBottomSheet(true)
      } else {
        setShowBottomSheet(false)
      }
    } else {
      if (targetHash) {
        showSidePopup()
      }
    }
  }, [currentHash, isMobile])

  const showSidePopup = () => {
    sidePopup.show({
      id: 'expression-popup',
      overlayClose: true,
      border: true,
      bgWhite: true,
      maxW: 405,
      hFull: true,
      gapFromTop: 112,
      noTopSide: true,
      onClose: () => {
        const cleanURL = window.location.href.split('#')[0]
        window.history.replaceState(null, null, cleanURL)

        setCurrentHash('')
        sidePopup.close('expression-popup')
      },
      body: (
        <ExpressionBody expressionData={materialData?.expressions} currentHash={currentHash} onClose={handleClose} />
      ),
    })
  }

  const handleClose = () => {
    // 현재 URL에서 # 및 이후의 내용을 제거합니다.
    const cleanURL = window.location.href.split('#')[0]
    window.history.replaceState(null, null, cleanURL)

    sidePopup.close('expression-popup')
    setShowBottomSheet(false)
    setCurrentHash('')
  }

  const handleCloseBottomSheet = () => {
    if (window) window.location.hash = ''
  }

  return (
    <div
      className={`px-[16px] lg:px-[0px] ${
        isLesson || isInsightButton || isModal ? '' : 'lg:max-w-[688px]'
      } no-scrollbar relative ${isWebview && 'overflow-y-scroll h-[100vh]'}`}
      ref={docRef}
    >
      {/* 교재 상단 타이틀 영역 */}
      <MaterialInsightTitleSection
        insightData={insightData}
        materialData={materialData}
        isLesson={isLesson}
        isModal={isModal}
      />
      {/* Practice */}
      {isPracticeCategory && (
        <div className="px-[24px] py-[12px] rounded-[10px] text-gray-500 text-[15px] font-semibold bg-gray-100 mt-[12px]">
          {t(s_materials.copyright_desc2)}
        </div>
      )}
      {/* 교재 내용 본문 영역 */}
      <MaterialTextBody insightData={insightData} materialData={materialData} isLoading={isLoading} isModal={isModal} />

      {/* 인사이트 읽기 버튼 영역 */}
      {isInsightButton && !isFreeTalkingOrWriting && (
        <InsightButtonReadButton materialData={materialData} setTabData={setTabData} isModal={isModal} />
      )}

      {/* 이 교재로 수업한 튜터 */}
      {!isModal && !isLesson && <MaterialTutorSection />}

      {/** 수업 준비 - 수업 리뷰 */}
      {(isInsightButton || isLesson) && <MediaPanel media={media} noMedia={noMedia} />}

      {/** 관련 교재 영역 */}
      {!isModal && <MaterialRelatedContents relatedContents={materialData?.course?.related_contents} />}

      {/* 모바일 : 핵심 표현 바텀 시트 영역 */}
      {showBottomSheet && (
        <DSBottomSheet
          enableScroll={true}
          id="ExpressionBottomSheet"
          isOpen={showBottomSheet}
          setIsOpen={setShowBottomSheet}
          closeButton={false}
          closeOnDim={true}
          closeOnDrag={true}
          onClose={handleCloseBottomSheet}
        >
          <ExpressionBody expressionData={materialData?.expressions} currentHash={currentHash} onClose={handleClose} />
        </DSBottomSheet>
      )}

      <EmptyBlock h={120} />
    </div>
  )
}

const areEqualContent = (prev: InsightPanelPropsType, next: InsightPanelPropsType) => {
  return (
    prev.materialData?.course?.summary_kor === next.materialData?.course?.summary_kor &&
    prev.materialData?.course?.summary_eng === next.materialData?.course?.summary_eng &&
    prev.materialData?.course?.title_en === next.materialData?.course?.title_en &&
    prev.materialData?.course?.title_kr === next.materialData?.course?.title_kr &&
    prev.materialData?.course?.title === next.materialData?.course?.title
  )
}

export default React.memo(MaterialInsightBody, areEqualContent)
